import React from 'react';
import { RuleModel } from 'src/types/rule.model';
import {
    getRuleInitialValues,
    getRuleName,
    getRuleValidationSchema,
    matchesRule,
    matchingRules
} from 'src/utils/rule.util';
import LiteralsService from 'src/services/literals/LiteralsService';
import { TransactionModel } from 'src/types/transaction.model';
import { Formik } from 'formik';
import RuleRow from 'src/components/atomic/molecules/ruleRow/RuleRow';
import { CategoryModel } from 'src/types/category.model';
import WarningDialog from 'src/components/atomic/molecules/warningDialog/WarningDialog';
import Selectors from 'src/utils/selectors';
import ruleStyles from 'src/styles/rule.module.scss';
import styles from './createUpdateRule.module.scss';
import { Alert } from '@americanexpress/dls-react';
import ApplyButton from 'src/components/atomic/atoms/applyButton/ApplyButton';
import DeleteButton from 'src/components/atomic/atoms/deleteButton/DeleteButton';

export interface CreateUpdateRuleFormikWrapperProps {
    rule: Partial<RuleModel>;
    rules: RuleModel[];
    ruleCategoryName?: string;
    pathname: string;
    transaction: TransactionModel;
    categories: CategoryModel[];
    handleSubmit: (rule: Partial<RuleModel>) => Promise<void>;
    handleDelete: (rule: RuleModel) => Promise<void>;
    isCreatingRule?: boolean;
    isMinWidthMedium?: boolean;
}

export default function CreateUpdateRuleFormikWrapper({
    rule,
    rules,
    ruleCategoryName,
    pathname,
    transaction,
    categories,
    isCreatingRule,
    isMinWidthMedium,
    handleSubmit,
    handleDelete
}: CreateUpdateRuleFormikWrapperProps): JSX.Element {
    const [showRuleAlreadyExistsError, setShowRuleAlreadyExistsError] = React.useState(false);

    return (
        <Formik
            initialValues={getRuleInitialValues(rule)}
            key={rule?.id}
            enableReinitialize
            validationSchema={getRuleValidationSchema()}
            onSubmit={async (values, { resetForm }) => {
                const submittedValues: Partial<RuleModel> = {
                    matcher: values.ruleMatcher,
                    transactionName: values.ruleTransactionName,
                    groupUid: values.ruleGroup
                };
                const doesRuleMatch = matchesRule(transaction, submittedValues);
                setShowRuleAlreadyExistsError(false);
                const newRule: Partial<RuleModel> =
                    isCreatingRule || !doesRuleMatch
                        ? {
                              ...submittedValues
                          }
                        : {
                              ...(rule as RuleModel),
                              ...submittedValues
                          };

                let error = false;
                rules?.forEach((existingRule) => {
                    if (matchingRules(newRule, existingRule) && existingRule.id !== newRule.id) {
                        error = true;
                        setShowRuleAlreadyExistsError(true);
                        return null;
                    }
                });

                if (!error) {
                    await handleSubmit(newRule);
                    if (!doesRuleMatch) {
                        void resetForm();
                    }
                }
            }}
        >
            {({ dirty, values }) => (
                <CreateUpdateRule
                    rule={rule}
                    ruleCategoryName={ruleCategoryName}
                    pathname={pathname}
                    categories={categories}
                    isMinWidthMedium={isMinWidthMedium}
                    showApplyButton={isCreatingRule || dirty}
                    showAlreadyExistsError={showRuleAlreadyExistsError}
                    showDoesNotMatchTransactionWarning={
                        dirty &&
                        !matchesRule(transaction, {
                            matcher: values.ruleMatcher,
                            transactionName: values.ruleTransactionName,
                            groupUid: values.ruleGroup
                        })
                    }
                    handleDelete={handleDelete}
                />
            )}
        </Formik>
    );
}

export interface CreateUpdateRuleProps {
    rule: Partial<RuleModel>;
    ruleCategoryName?: string;
    showApplyButton?: boolean;
    showAlreadyExistsError?: boolean;
    showDoesNotMatchTransactionWarning?: boolean;
    pathname: string;
    categories: CategoryModel[];
    isMinWidthMedium?: boolean;
    handleDelete: (rule: RuleModel) => Promise<void>;
}

export function CreateUpdateRule({
    rule,
    ruleCategoryName,
    pathname,
    categories,
    showApplyButton,
    showAlreadyExistsError,
    showDoesNotMatchTransactionWarning,
    isMinWidthMedium,
    handleDelete
}: CreateUpdateRuleProps): JSX.Element {
    const applyButton: JSX.Element = (
        <ApplyButton
            showText
            data-cy={Selectors.Transactions.APPLY_RULE_BUTTON}
            aria-label={LiteralsService.getLiteral('Rule.applyRule')}
            className={ruleStyles.applyButton}
        />
    );

    const deleteButton: JSX.Element = (
        <DeleteButton
            text={!isMinWidthMedium && LiteralsService.getLiteral('Rule.deleteRule')}
            aria-label={LiteralsService.getLiteral('Rule.deleteRule')}
            data-cy={Selectors.Transactions.DELETE_RULE_BUTTON}
            onClick={async () => await handleDelete(rule as RuleModel)}
            className={ruleStyles.deleteButton}
        />
    );

    return (
        <div data-cy={Selectors.Transactions.CREATE_UPDATE_RULE} className={styles.createUpdateRule}>
            <RuleRow
                rule={rule}
                categories={categories}
                pathname={pathname}
                ariaLabel={getRuleName(rule, ruleCategoryName)}
                button={showApplyButton ? applyButton : deleteButton}
            />
            {showAlreadyExistsError ? (
                <Alert data-cy={Selectors.Transactions.RULE_ALREADY_EXISTS_ERROR} id="ruleAlreadyExistsAlert">
                    {LiteralsService.getLiteral('Rule.alreadyExists')}
                </Alert>
            ) : (
                showDoesNotMatchTransactionWarning && (
                    <WarningDialog text={LiteralsService.getLiteral('Rule.doesNotMatchTransaction')} />
                )
            )}
        </div>
    );
}
